import React from 'react';
import { osc, wobble, drawOn, loop, clamp, lerp, easeOut, easeInOut } from '../../core/AnimationUtils';

/*
 * Concept-acting SCENES — Computer Science edition (Anivaze v2)
 * The concept HAPPENS in front of you: no diagrams, no bullet-point labels.
 * Kindergarten-friendly, pure function of `t`. See design-plan/v2/ANIMATION-PATTERN.md.
 *
 * Exports
 *   AlgorithmScene  — making a sandwich step-by-step = an algorithm
 *   SortingScene    — five colourful bars swap into sorted order = sorting
 */

/* ─── Shared helpers ─────────────────────────────────────────────────────── */

/** Round-rect path helper (SVG <path> d-string). */
const rr = (x: number, y: number, w: number, h: number, r: number): string =>
  `M${x + r},${y} h${w - 2 * r} a${r},${r} 0 0 1 ${r},${r} v${h - 2 * r} a${r},${r} 0 0 1 ${-r},${r} h${-(w - 2 * r)} a${r},${r} 0 0 1 ${-r},${-r} v${-(h - 2 * r)} a${r},${r} 0 0 1 ${r},${-r} Z`;

/** Small smiling face centred at (cx, cy). */
const HappyFace = (cx: number, cy: number, scale: number, blinkT: number) => {
  const eyeOpen = osc(blinkT, 3.8, 0, 1, 0) > 0.08; // blink ~every 3.8s
  return (
    <g transform={`translate(${cx} ${cy}) scale(${scale})`}>
      {/* head */}
      <circle r={32} fill="#fde68a" stroke="#b45309" strokeWidth={2.5} />
      {/* eyes */}
      {eyeOpen ? (
        <>
          <circle cx={-11} cy={-6} r={5} fill="#1f2937" />
          <circle cx={11} cy={-6} r={5} fill="#1f2937" />
          <circle cx={-9} cy={-8} r={2} fill="#fff" />
          <circle cx={13} cy={-8} r={2} fill="#fff" />
        </>
      ) : (
        <>
          <path d="M-16,-6 Q-11,-2 -6,-6" fill="none" stroke="#1f2937" strokeWidth={3} strokeLinecap="round" />
          <path d="M6,-6 Q11,-2 16,-6" fill="none" stroke="#1f2937" strokeWidth={3} strokeLinecap="round" />
        </>
      )}
      {/* smile */}
      <path d="M-14,10 Q0,24 14,10" fill="none" stroke="#b45309" strokeWidth={3} strokeLinecap="round" />
      {/* rosy cheeks */}
      <ellipse cx={-20} cy={6} rx={7} ry={5} fill="#fca5a5" opacity={0.6} />
      <ellipse cx={20} cy={6} rx={7} ry={5} fill="#fca5a5" opacity={0.6} />
    </g>
  );
};

/* ━━━ AlgorithmScene ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * A numbered recipe on the left; a hand builds the sandwich layer-by-layer
 * in sync as each step highlights. Cycle ~10s, loops seamlessly.
 * "Step-by-step, order matters" is the whole point.
 */
export interface AlgorithmSceneProps {
  opacity?: number;
  accentColor?: string;
  label?: string;
  /** Seconds since the scene started. Pure function of t — no Date/state. */
  t?: number;
}

export const AlgorithmScene: React.FC<AlgorithmSceneProps> = ({
  opacity = 1,
  accentColor = '#f59e0b',
  t = 0,
  label = 'An algorithm is a step-by-step recipe!',
}) => {
  // One full sandwich-build cycle = 10s, then repeat forever.
  const CYCLE = 10;
  const phase = loop(t, CYCLE);            // 0..1 through the cycle
  const stepSec = CYCLE / 5;              // 2s per step

  // Which step (0-4) is currently "active" in the cycle.
  const activeStep = Math.min(4, Math.floor(phase * 5));

  // Progress within the current step (0→1).
  const stepP = (phase * 5) % 1;

  // For perfectly looping layer reveals: treat t as-is but re-arm each cycle.
  const cycleOffset = Math.floor(t / CYCLE) * CYCLE;
  const lp = (stepIdx: number): number =>
    clamp(drawOn(t - cycleOffset - stepIdx * stepSec, stepSec * 0.55), 0, 1);

  // Sandwich assembly area (right side)
  const SX = 520;  // sandwich centre-x
  const BASE_Y = 370; // ground line for the sandwich stack

  // Layer definitions: colour, height, label
  const layers = [
    { color: '#fde68a', h: 18, name: 'Bottom bread',  emoji: '🍞' },
    { color: '#fbbf24', h: 14, name: 'Peanut butter', emoji: '🥜' },
    { color: '#dc2626', h: 18, name: 'Strawberry jam',emoji: '🍓' },
    { color: '#bbf7d0', h: 14, name: 'Lettuce',       emoji: '🥬' },
    { color: '#fde68a', h: 20, name: 'Top bread',     emoji: '🍞' },
  ];

  // Stack bottom-up: each layer sits on top of what came before.
  // Cumulative heights to figure out y-positions.
  const cumH = layers.reduce<number[]>((acc, l, i) => {
    acc.push((acc[i - 1] ?? 0) + l.h + 2);
    return acc;
  }, []);

  // Hand position: hovers above the layer being placed, then descends.
  const handTargetY = BASE_Y - (cumH[activeStep] ?? 0) - 32;
  const handY = handTargetY + (1 - stepP) * 40 * (activeStep < 5 ? 1 : 0);
  const handX = SX + 10;

  // "Done!" badge appears after all 5 layers are placed (last 15% of cycle).
  const doneFrac = clamp((phase - 0.85) / 0.15, 0, 1);

  // Recipe panel (left side)
  const RECIPE_X = 60;
  const RECIPE_Y = 80;
  const stepLabels = ['1. Bottom bread', '2. Spread it!', '3. Add filling', '4. Add lettuce', '5. Top slice'];

  return (
    <g opacity={opacity}>
      {/* Background */}
      <rect x={0} y={0} width={800} height={450} fill="#fffbeb" />

      {/* ── Recipe card (left) ─────────────────────────────────── */}
      <g>
        {/* card background */}
        <path d={rr(RECIPE_X - 16, RECIPE_Y - 28, 290, 280, 16)} fill="#fff" stroke="#fcd34d" strokeWidth={2.5} />
        {/* card title */}
        <text x={RECIPE_X + 127} y={RECIPE_Y - 4} textAnchor="middle" fill="#92400e"
          fontSize={16} fontWeight={800} fontFamily="Inter,sans-serif">Recipe 📜</text>

        {stepLabels.map((lbl, i) => {
          const isActive = i === activeStep;
          const isDone = i < activeStep;
          const rowY = RECIPE_Y + 24 + i * 46;
          const textOpacity = isDone ? 0.55 : 1;

          return (
            <g key={i}>
              {/* highlight pill for active step */}
              {isActive && (
                <path
                  d={rr(RECIPE_X - 8, rowY - 16, 274, 38, 10)}
                  fill={accentColor}
                  opacity={0.25 + osc(t, 0.8, 0, 0.15)}
                />
              )}
              {/* step row */}
              <g opacity={textOpacity}>
                {/* number circle */}
                <circle cx={RECIPE_X + 12} cy={rowY + 3} r={14}
                  fill={isDone ? '#86efac' : isActive ? accentColor : '#e5e7eb'}
                  stroke={isDone ? '#16a34a' : isActive ? '#b45309' : '#d1d5db'}
                  strokeWidth={2} />
                <text x={RECIPE_X + 12} y={rowY + 8} textAnchor="middle"
                  fill={isDone ? '#166534' : isActive ? '#7c2d12' : '#6b7280'}
                  fontSize={12} fontWeight={800} fontFamily="Inter,sans-serif">
                  {isDone ? '✓' : `${i + 1}`}
                </text>
                {/* step label */}
                <text x={RECIPE_X + 34} y={rowY + 8}
                  fill={isActive ? '#92400e' : '#374151'}
                  fontSize={14} fontWeight={isActive ? 700 : 500}
                  fontFamily="Inter,sans-serif">{lbl}</text>
              </g>
            </g>
          );
        })}
      </g>

      {/* ── Sandwich assembly (right) ──────────────────────────── */}
      <g>
        {/* kitchen counter */}
        <path d={rr(390, 378, 280, 30, 8)} fill="#d1d5db" stroke="#9ca3af" strokeWidth={2} />

        {/* layers: draw bottom-up */}
        {layers.map((layer, i) => {
          const p = lp(i);
          if (p <= 0) return null;
          const totalBelow = i === 0 ? 0 : cumH[i - 1];
          const layerY = BASE_Y - totalBelow - layer.h;
          const slideY = layerY - (1 - p) * 28;  // slides down into place
          const w = 180 - i * 4;                  // taper slightly toward top

          return (
            <g key={i} opacity={p}>
              {/* layer body */}
              <path
                d={rr(SX - w / 2, slideY, w, layer.h, layer.h / 2 - 2)}
                fill={layer.color}
                stroke="#92400e"
                strokeWidth={1.5}
              />
              {/* tiny ingredient icon at right edge */}
              <text x={SX + w / 2 - 20} y={slideY + layer.h - 3}
                fontSize={13} textAnchor="middle">{layer.emoji}</text>
            </g>
          );
        })}

        {/* Hand / arm descends to place each layer */}
        {activeStep < 5 && (
          <g transform={`translate(${handX} ${handY})`} opacity={clamp(1 - doneFrac * 3, 0, 1)}>
            {/* arm */}
            <rect x={-6} y={0} width={12} height={44} rx={6} fill="#fcd34d" stroke="#b45309" strokeWidth={1.5} />
            {/* fingers */}
            {[-10, -4, 2, 8].map((dx, fi) => (
              <rect key={fi} x={dx} y={38} width={8} height={18} rx={4}
                fill="#fde68a" stroke="#b45309" strokeWidth={1.5} />
            ))}
            {/* sparkle when placing */}
            {stepP < 0.35 && [0, 1, 2].map(k => {
              const ang = (k / 3) * Math.PI * 2 + t * 4;
              const sr = 22 + k * 6;
              return (
                <text key={k} x={Math.cos(ang) * sr} y={Math.sin(ang) * sr}
                  fontSize={10} textAnchor="middle" opacity={0.8}>✨</text>
              );
            })}
          </g>
        )}

        {/* "Done!" badge */}
        {doneFrac > 0 && (
          <g opacity={easeOut(doneFrac)} transform={`translate(${SX} ${BASE_Y - 130})`}>
            <path d={rr(-58, -26, 116, 46, 14)} fill="#dcfce7" stroke="#16a34a" strokeWidth={2.5} />
            <text x={0} y={4} textAnchor="middle" fill="#166534"
              fontSize={19} fontWeight={800} fontFamily="Inter,sans-serif">Done! 🎉</text>
          </g>
        )}

        {/* Happy character to the right of the sandwich */}
        {HappyFace(720, 355, 0.85, t)}
      </g>

      {/* ── Caption ────────────────────────────────────────────── */}
      <g transform="translate(400 422)">
        <path d={rr(-260, -20, 520, 36, 18)} fill="rgba(255,255,255,0.9)" stroke="#fcd34d" strokeWidth={2} />
        <text x={0} y={4} textAnchor="middle" fill="#0f172a"
          fontSize={15} fontWeight={700} fontFamily="Inter,sans-serif">{label}</text>
      </g>
    </g>
  );
};

/* ━━━ SortingScene ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * Five colourful bars start jumbled. Bubble-sort swaps animate one at a time:
 * two bars hop over each other with arc trajectories. ~8s cycle, seamless loop.
 * Ends with a rainbow staircase + "Sorted!" checkmark.
 */
export interface SortingSceneProps {
  opacity?: number;
  accentColor?: string;
  label?: string;
  /** Seconds since the scene started. Pure function of t — no Date/state. */
  t?: number;
}

export const SortingScene: React.FC<SortingSceneProps> = ({
  opacity = 1,
  accentColor = '#6366f1',
  t = 0,
  label = 'Sorting puts things in order!',
}) => {
  // Bar definitions: value, colour (deterministic — no Math.random()).
  const BAR_COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#6366f1'];
  const BAR_VALUES = [4, 1, 3, 0, 2];      // initial shuffle (indices into sorted order)

  // Bubble sort swap sequence (pre-computed, deterministic).
  // For 5 elements [4,1,3,0,2] → sorted requires these swaps (i = left index):
  //   pass 1: swap(0,1)→[1,4,3,0,2], swap(1,2)→[1,3,4,0,2], swap(2,3)→[1,3,0,4,2], swap(3,4)→[1,3,0,2,4]
  //   pass 2: swap(1,2)→[1,0,3,2,4], swap(2,3)→[1,0,2,3,4]
  //   pass 3: swap(0,1)→[0,1,2,3,4]
  const SWAPS: Array<[number, number]> = [
    [0, 1], [1, 2], [2, 3], [3, 4],
    [1, 2], [2, 3],
    [0, 1],
  ];
  const N_SWAPS = SWAPS.length; // 7

  const CYCLE = 8;              // seconds for one full sort + pause + restart
  const SWAP_DUR = 0.7;         // seconds each swap arc takes

  const phase = loop(t, CYCLE);                     // 0..1 through the cycle
  const cycleT = phase * CYCLE;                     // seconds into current cycle

  // After all swaps finish, how long do we hold? work out timing.
  const SORT_END = N_SWAPS * SWAP_DUR;              // ~4.9s

  // Build current bar positions by replaying swaps completed so far.
  const positions = [...BAR_VALUES];                // mutable copy for replay
  const swapsDone = Math.min(N_SWAPS, Math.floor(cycleT / SWAP_DUR));
  for (let s = 0; s < swapsDone; s++) {
    const [a, b] = SWAPS[s];
    const tmp = positions[a]; positions[a] = positions[b]; positions[b] = tmp;
  }

  // Current swap in-progress (if any).
  const swapIdx = swapsDone < N_SWAPS ? swapsDone : -1;
  const swapFrac = swapIdx >= 0 ? clamp((cycleT - swapsDone * SWAP_DUR) / SWAP_DUR, 0, 1) : 0;

  // Bar geometry
  const BAR_W = 80;
  const BAR_GAP = 20;
  const TOTAL_W = 5 * BAR_W + 4 * BAR_GAP;
  const START_X = (800 - TOTAL_W) / 2;  // centre the row
  const FLOOR_Y = 360;
  const BAR_HEIGHTS = [120, 70, 160, 50, 100];      // pixel heights (deterministic)

  // x-centre of slot i
  const slotX = (i: number): number => START_X + i * (BAR_W + BAR_GAP) + BAR_W / 2;

  // For each physical bar index, compute its visual x (may be mid-arc).
  const barX = Array.from({ length: 5 }, (_, bi) => {
    // Which slot is this bar currently in?
    const slot = positions.indexOf(bi);

    // If this bar is one of the two currently swapping, arc it.
    if (swapIdx >= 0) {
      const [sa, sb] = SWAPS[swapIdx];
      const barInSlotA = positions[sa] === bi;
      const barInSlotB = positions[sb] === bi;
      if (barInSlotA || barInSlotB) {
        const fromSlot = barInSlotA ? sa : sb;
        const toSlot = barInSlotA ? sb : sa;
        const ex = easeInOut(swapFrac);
        const arcHeight = barInSlotA ? -55 : -38;    // left bar arcs higher
        const midY = arcHeight * Math.sin(swapFrac * Math.PI);
        return { x: lerp(slotX(fromSlot), slotX(toSlot), ex), arcY: midY };
      }
    }

    return { x: slotX(slot), arcY: 0 };
  });

  // "Sorted!" reveal: fades in after all swaps done.
  const sortedFrac = clamp((cycleT - SORT_END) / 0.6, 0, 1);
  const isSorted = cycleT >= SORT_END;

  // Rainbow staircase colouring when sorted.
  const rainbowOpacity = easeOut(sortedFrac);

  // Sparkle particles that burst when sorted arrives.
  const sparkleFrac = clamp((cycleT - SORT_END) / 0.4, 0, 1);
  const NUM_SPARKS = 8;

  return (
    <g opacity={opacity}>
      {/* Background */}
      <rect x={0} y={0} width={800} height={450} fill="#f0f9ff" />

      {/* Floor */}
      <path d={rr(30, FLOOR_Y + 14, 740, 18, 6)} fill="#cbd5e1" />

      {/* Sort progress label (top-left) */}
      <g opacity={swapsDone < N_SWAPS ? 1 : 0}>
        <text x={40} y={42} fill="#64748b" fontSize={13} fontWeight={600}
          fontFamily="Inter,sans-serif">
          Swap {swapsDone + (swapIdx >= 0 ? 1 : 0)} of {N_SWAPS}
        </text>
      </g>

      {/* Swap-arc guide lines (faint) */}
      {swapIdx >= 0 && (() => {
        const [sa, sb] = SWAPS[swapIdx];
        const ax = slotX(sa), bx = slotX(sb);
        const midX = (ax + bx) / 2;
        const arcY = FLOOR_Y - BAR_HEIGHTS[positions[sa]] - 65;
        return (
          <path
            d={`M${ax},${FLOOR_Y - BAR_HEIGHTS[positions[sa]]} Q${midX},${arcY} ${bx},${FLOOR_Y - BAR_HEIGHTS[positions[sb]]}`}
            fill="none" stroke={accentColor} strokeWidth={2} strokeDasharray="5 5"
            opacity={0.3 + swapFrac * 0.3}
          />
        );
      })()}

      {/* Bars */}
      {Array.from({ length: 5 }, (_, bi) => {
        const value = bi;                       // bar bi represents sorted rank bi
        const h = BAR_HEIGHTS[bi];
        const col = BAR_COLORS[bi];
        const { x, arcY } = barX[bi];
        const y = FLOOR_Y - h + arcY;

        // Rainbow border when sorted
        const strokeCol = isSorted ? BAR_COLORS[bi] : '#94a3b8';
        const strokeW = isSorted ? 3 : 1.5;

        // Sparkle on sorted arrival
        const bobble = isSorted ? wobble(t, 1.2 + bi * 0.18, 4, bi * 1.1) : 0;

        return (
          <g key={bi}>
            {/* bar body */}
            <path
              d={rr(x - BAR_W / 2, y + bobble, BAR_W, h, 10)}
              fill={col}
              stroke={strokeCol}
              strokeWidth={strokeW}
              opacity={0.88 + rainbowOpacity * 0.12}
            />
            {/* value label inside bar */}
            <text x={x} y={y + bobble + 28} textAnchor="middle"
              fill="#fff" fontSize={20} fontWeight={800} fontFamily="Inter,sans-serif">
              {value + 1}
            </text>
            {/* height tick at top */}
            <line x1={x - BAR_W / 2 + 6} y1={y + bobble} x2={x + BAR_W / 2 - 6} y2={y + bobble}
              stroke="#fff" strokeWidth={2.5} strokeLinecap="round" opacity={0.5} />
          </g>
        );
      })}

      {/* Swap arrows: little down-arrows above the two swapping bars */}
      {swapIdx >= 0 && (() => {
        const [sa, sb] = SWAPS[swapIdx];
        return (
          <>
            {[sa, sb].map(si => {
              const bv = positions[si];
              const cx = slotX(si);
              const ty = FLOOR_Y - BAR_HEIGHTS[bv] - 30;
              return (
                <g key={si} opacity={clamp(Math.sin(swapFrac * Math.PI) * 1.4, 0, 1)}>
                  <text x={cx} y={ty} textAnchor="middle" fontSize={20}>🔄</text>
                </g>
              );
            })}
          </>
        );
      })()}

      {/* Sorted burst: sparkles */}
      {isSorted && Array.from({ length: NUM_SPARKS }, (_, k) => {
        const ang = (k / NUM_SPARKS) * Math.PI * 2;
        const r = 80 + k * 12;
        const sparkX = 400 + Math.cos(ang) * r * sparkleFrac;
        const sparkY = 200 + Math.sin(ang) * r * sparkleFrac;
        const sparkOpacity = clamp(1 - sparkleFrac * 0.8, 0, 1);
        return (
          <text key={k} x={sparkX} y={sparkY} textAnchor="middle"
            fontSize={14 + (k % 3) * 4} opacity={sparkOpacity}>
            {['⭐', '✨', '🌟', '💫'][k % 4]}
          </text>
        );
      })}

      {/* "Sorted!" badge */}
      {isSorted && (
        <g opacity={easeOut(sortedFrac)} transform="translate(400 130)">
          <path d={rr(-80, -30, 160, 56, 16)} fill="#dcfce7" stroke="#16a34a" strokeWidth={3} />
          <text x={0} y={-4} textAnchor="middle" fill="#166534"
            fontSize={22} fontWeight={800} fontFamily="Inter,sans-serif">✓ Sorted!</text>
          <text x={0} y={18} textAnchor="middle" fill="#15803d"
            fontSize={13} fontWeight={600} fontFamily="Inter,sans-serif">shortest → tallest</text>
        </g>
      )}

      {/* Swap-in-progress label */}
      {swapIdx >= 0 && (
        <g opacity={clamp(Math.sin(swapFrac * Math.PI) * 2, 0, 1)}
          transform={`translate(400 60)`}>
          <path d={rr(-70, -16, 140, 30, 10)} fill="rgba(255,255,255,0.85)" stroke={accentColor} strokeWidth={1.5} />
          <text x={0} y={2} textAnchor="middle" fill="#1e1b4b"
            fontSize={13} fontWeight={700} fontFamily="Inter,sans-serif">swap! 🔀</text>
        </g>
      )}

      {/* Caption */}
      <g transform="translate(400 422)">
        <path d={rr(-240, -20, 480, 36, 18)} fill="rgba(255,255,255,0.9)" stroke="#a5b4fc" strokeWidth={2} />
        <text x={0} y={4} textAnchor="middle" fill="#0f172a"
          fontSize={15} fontWeight={700} fontFamily="Inter,sans-serif">{label}</text>
      </g>
    </g>
  );
};
